import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, ExternalLink, X } from "lucide-react";
import Shot from "./Shot";
import BarChart from "./BarChart";
import { findProject, profile, projects, t, ui } from "../data/portfolio";

export default function ProjectPage({ slug, lang, onAsk }) {
  const labels = ui[lang];
  const project = findProject(slug);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLightbox(null);
  }, [slug]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox]);

  if (!project) {
    return (
      <main className="wrap project-missing">
        <p>404</p>
        <a className="btn" href="#/">
          {labels.backHome}
        </a>
      </main>
    );
  }

  const index = projects.findIndex((p) => p.slug === slug);
  const next = projects[(index + 1) % projects.length];
  const name = t(project.name, lang);
  const gallery = project.gallery || [];
  const detail = project.detail || {};
  const highlights = detail.highlights || [];

  return (
    <main className="wrap project">
      <a className="project-back" href="#/">
        <ArrowLeft size={15} strokeWidth={1.9} />
        {labels.backHome}
      </a>

      <header className="project-head">
        <div className="project-meta">
          <span>{project.year}</span>
          <span className="project-org">{t(project.org, lang)}</span>
          <span className="project-kind">{t(project.kind, lang)}</span>
          {project.status && (
            <span
              className={`project-status ${project.live ? "is-live" : ""} ${
                project.beta ? "is-beta" : ""
              }`}
            >
              {t(project.status, lang)}
            </span>
          )}
        </div>

        <h1 className="project-name">{name}</h1>
        <p className="project-tagline">{t(project.tagline, lang)}</p>
      </header>

      {gallery.length > 0 && (
        <section className="gallery" aria-label={labels.galleryLabel}>
          <figure className="gallery-lead">
            <Shot
              src={gallery[0].src}
              name={name}
              lang={lang}
              ratio={gallery[0].ratio || "16 / 9"}
              caption={gallery[0].caption}
              onClick={() => setLightbox(0)}
            />
            {gallery[0].caption && <figcaption>{t(gallery[0].caption, lang)}</figcaption>}
          </figure>

          {gallery.length > 1 && (
            <div className={`gallery-rest ${project.galleryWide ? "gallery-rest--wide" : ""}`}>
              {gallery.slice(1).map((shot, i) => (
                <figure key={shot.src}>
                  <Shot
                    src={shot.src}
                    name={name}
                    lang={lang}
                    ratio={shot.ratio || "4 / 3"}
                    caption={shot.caption}
                    onClick={() => setLightbox(i + 1)}
                  />
                  {shot.caption && <figcaption>{t(shot.caption, lang)}</figcaption>}
                </figure>
              ))}
            </div>
          )}
        </section>
      )}

      {project.metrics && project.metrics.length > 0 && (
        <section className="project-metrics">
          {project.metrics.map((m) => (
            <div className="project-metric" key={m.value + t(m.label, lang)}>
              <span className="project-metric-value">{m.value}</span>
              <span className="project-metric-label">{t(m.label, lang)}</span>
            </div>
          ))}
        </section>
      )}

      {project.chart && <BarChart chart={project.chart} lang={lang} />}

      <div className="project-body">
        <article className="project-prose">
          {detail.overview && (
            <section className="prose-block">
              <h2>{labels.overviewLabel}</h2>
              <p>{t(detail.overview, lang)}</p>
            </section>
          )}

          {detail.problem && (
            <section className="prose-block">
              <h2>{labels.problemLabel}</h2>
              <p>{t(detail.problem, lang)}</p>
            </section>
          )}

          {detail.solution && (
            <section className="prose-block">
              <h2>{labels.solutionLabel}</h2>
              <p>{t(detail.solution, lang)}</p>
            </section>
          )}

          {highlights.length > 0 && (
            <section className="prose-block">
              <h2>{labels.highlightsLabel}</h2>
              <ul className="highlights">
                {highlights.map((h) => (
                  <li key={t(h.title, lang)}>
                    <h3>{t(h.title, lang)}</h3>
                    <p>{t(h.body, lang)}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {detail.outcome && (
            <section className="prose-block prose-block--outcome">
              <h2>{labels.outcomeLabel}</h2>
              <p>{t(detail.outcome, lang)}</p>
            </section>
          )}
        </article>

        <aside className="project-aside">
          <div className="aside-block">
            <h2>{labels.stackLabel}</h2>
            <ul className="stack-list">
              {project.stack.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>

          {project.links && project.links.length > 0 && (
            <div className="aside-block">
              <h2>{labels.linksLabel}</h2>
              <ul className="link-list">
                {project.links.map((l) => (
                  <li key={l.href}>
                    <a href={l.href} target="_blank" rel="noreferrer">
                      {t(l.label, lang)}
                      <ExternalLink size={13} strokeWidth={1.9} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="aside-block aside-ask">
            <p>
              {lang === "tr"
                ? "Bu proje hakkında aklına takılan bir şey mi var?"
                : "Something you want to know about this project?"}
            </p>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() =>
                onAsk(
                  lang === "tr"
                    ? `${name} projesini biraz daha anlatır mısın?`
                    : `Can you tell me more about ${name}?`
                )
              }
            >
              {labels.palette.ask}
            </button>
            <a className="btn btn--ghost" href={`mailto:${profile.email}`}>
              {labels.contact.emailAction}
            </a>
          </div>
        </aside>
      </div>

      <a className="project-next" href={`#/proje/${next.slug}`}>
        <span className="project-next-label">{labels.nextProject}</span>
        <span className="project-next-name">
          {t(next.name, lang)}
          <ArrowRight size={18} strokeWidth={1.9} />
        </span>
      </a>

      {lightbox !== null && gallery[lightbox] && (
        <div className="lightbox" onClick={() => setLightbox(null)} role="dialog" aria-modal="true">
          <button type="button" className="lightbox-close" aria-label="Kapat">
            <X size={20} strokeWidth={1.9} />
          </button>
          <img src={gallery[lightbox].src} alt={name} onClick={(e) => e.stopPropagation()} />
          {gallery[lightbox].caption && (
            <p className="lightbox-caption">{t(gallery[lightbox].caption, lang)}</p>
          )}
        </div>
      )}
    </main>
  );
}
